// src/pages/Checkout.jsx

import { useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { fetchShippingRate, createCheckoutSession } from "./api";
import React, { useEffect, useState } from "react";
gsap.registerPlugin(ScrollTrigger);

// 👉 Put your publishable key in .env
// CRA: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
// Vite: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY



export default function Checkout() {
  const location = useLocation();
  // Dynamically fetch publishable key from backend
  const [stripeKey, setStripeKey] = useState("");
  useEffect(() => {
    fetch("/api/stripe-pk").then(r => r.json()).then(d => setStripeKey(d.key || ""));
  }, []);

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware", "District of Columbia", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

  const cart = (location && location.state) || {};
  // IMPORTANT: Add your Stripe Price ID here (from Stripe dashboard)
  const product = cart.product || { name: "Dope Shot", price: 36, image: "/dopeshoot-model3.png", priceId: "price_1SIOYoQ2jusiVrGtTNV7AmnQ" };
  const packs = cart.packs || 1;
  const packSize = cart.packSize || 1;

  const [form, setForm] = useState({
    email: "",
    fullName: "",
    address1: "",
    address2: "",
    city: "",
    state: "CA",
    postalCode: "",
    coupon: ""
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [shipping, setShipping] = useState({ zone: null, rate: 0 });

  // Animate in
  useEffect(() => {
    const shell = document.querySelector(".ck-shell");
    const card = document.querySelector(".ck-card");
    gsap.set(shell, { autoAlpha: 0 });
    gsap.set(card, { y: 20, autoAlpha: 0, scale: 0.98 });
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    tl.to(shell, { autoAlpha: 1, duration: 0.2 })
      .to(card, { y: 0, autoAlpha: 1, scale: 1, duration: 0.35 }, "<");
  }, []);


  const productCents = Math.round(product.price * 100) * packs; // price per pack
  const shippingCents = form.postalCode && shipping.rate ? Math.round((shipping.rate || 0) * 100) : 0;
  const totalCents = productCents + shippingCents;


  // Update shipping when any location field changes and ZIP is valid
  const onChange = async (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      // Always update shipping if ZIP is 5 digits
      if (updated.postalCode && updated.postalCode.length === 5 && /^[0-9]{5}$/.test(updated.postalCode)) {
        fetchShippingRate(updated.postalCode, packs)
          .then(setShipping)
          .catch(() => setShipping({ zone: null, rate: 0 }));
      } else {
        setShipping({ zone: null, rate: 0 });
      }
      return updated;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      // Prevent checkout if shipping is not available
      if (!shipping.rate || shipping.rate <= 0) {
        throw new Error("Shipping rate unavailable for this ZIP code. Please check your address.");
      }
      if (!product.priceId) throw new Error("Stripe Price ID missing in product. Please contact support.");
      const { url, error: apiError } = await createCheckoutSession({
        product,
        packs,
        shippingZip: form.postalCode,
        coupon: form.coupon
      });
      if (apiError) throw new Error(apiError);
      if (!url) throw new Error("Stripe session URL missing.");
      window.location.href = url;
    } catch (err) {
      setError(err.message || "Failed to create checkout session. Please check your shipping address and try again.");
      setBusy(false);
    }
  };

  const fmt = (cents) => (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

  return (
    <main className="ck">
      <div className="ck-shell">
        <div className="ck-card">
          <div className="ck-grid">
            {/* LEFT: form */}
            <section className="ck-left">
              <h2>Shipping</h2>
              <form onSubmit={submit} className="ck-form">
                <label>
                  Email
                  <input name="email" type="email" required value={form.email} onChange={onChange} />
                </label>
                <label>
                  Full name
                  <input name="fullName" required value={form.fullName} onChange={onChange} />
                </label>
                <div className="grid2">
                  <label>
                    Address line 1
                    <input name="address1" required value={form.address1} onChange={onChange} />
                  </label>
                  <label>
                    Address line 2 (optional)
                    <input name="address2" value={form.address2} onChange={onChange} />
                  </label>
                </div>
                <div className="grid3">
                  <label>
                    City
                    <input name="city" required value={form.city} onChange={onChange} />
                  </label>
                  <label>
                    State
                    <select name="state" required value={form.state} onChange={onChange}>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <label>
                    ZIP
                    <input name="postalCode" inputMode="numeric" required value={form.postalCode} onChange={onChange} />
                  </label>
                </div>

                <label>
                  Coupon code (optional)
                  <input name="coupon" value={form.coupon} onChange={onChange} placeholder="Enter coupon code" />
                </label>

                {error && <p className="ck-error">{error}</p>}

                <button className="ck-pay" disabled={busy}>
                  {busy ? "Redirecting…" : "Pay with Stripe"}
                </button>
                <p className="ck-note">We ship only within the United States.</p>
              </form>
            </section>

            {/* RIGHT: summary */}
            <aside className="ck-right">
              <div className="summary">
                <div className="prod">
                  <img src={product.image} alt={product.name} />
                  <div>
                    <h3>{product.name}</h3>
                    <p>${product.price.toFixed(2)} / pack</p>
                    <p className="muted">Quantity: {packs} {packs > 1 ? "packs" : "pack"}</p>
                  </div>
                </div>

                <hr />

                <div className="row">
                  <span>Subtotal</span>
                  <strong>{fmt(productCents)}</strong>
                </div>

                <div className="row">
                  <span>Shipping {shipping.zone ? `(Zone ${shipping.zone})` : ""}</span>
                  <strong>{fmt(shippingCents)}</strong>
                </div>

                <div className="row total">
                  <span>Total</span>
                  <strong>{fmt(totalCents)}</strong>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>

      <style>{`
        *,*::before,*::after{box-sizing:border-box}
        .ck{--bg:#070a0f;--fg:#f5f7fb;--muted:#c6cddd;--line:rgba(255,255,255,.16);--soft:rgba(255,255,255,.06);--card:#0e1420;
             min-height:100vh;background:radial-gradient(50% 30% at 75% 10%, rgba(111,209,255,.08), transparent 60%), #070a0f; color:var(--fg)}
        .ck-shell{max-width:1100px;margin:0 auto;padding:clamp(20px,4vw,40px);opacity:0}
        .ck-card{background:linear-gradient(180deg,rgba(255,255,255,.06),rgba(255,255,255,.02));
                 border:1px solid rgba(255,255,255,.1); border-radius:18px; box-shadow:0 12px 36px rgba(0,0,0,.45); padding:clamp(16px,3vw,26px); opacity:0}
        .ck-grid{display:grid;grid-template-columns:1.1fr .9fr; gap:clamp(18px,3vw,28px)}
        .ck-left h2{margin:0 0 10px}
        label{display:grid; gap:6px; font-size:14px; color:var(--muted)}
        input, select{
          background:#0c1320; color:#fff; border:1px solid rgba(255,255,255,.14); border-radius:10px;
          padding:12px 12px; outline:none; transition:border .2s ease, box-shadow .2s ease;
        }
        input:focus, select:focus{border-color:#fff; box-shadow:0 0 0 3px rgba(255,255,255,.2)}
        .grid2{display:grid; grid-template-columns:1fr 1fr; gap:14px}
        .grid3{display:grid; grid-template-columns:1fr 170px 190px; gap:14px}
        .ck-pay{
          margin-top:14px; width:100%; padding:12px 16px; border-radius:12px; border:1px solid rgba(255,255,255,.14);
          background:linear-gradient(180deg, rgba(255,255,255,.12), rgba(255,255,255,.04));
          color:#fff; font-weight:700; letter-spacing:.3px; cursor:pointer;
          transition:transform .15s ease, box-shadow .15s ease, background .15s ease;
        }
        .ck-pay:hover{transform:translateY(-1px); box-shadow:0 10px 28px rgba(0,0,0,.45)}
        .ck-pay:disabled{opacity:.6; cursor:not-allowed}
        .ck-note{margin:8px 0 0; font-size:12px; color:var(--muted)}
        .ck-error{margin:8px 0 0; color:#ff9a9a; font-weight:600}

        .ck-right .summary{
          border:1px solid rgba(255,255,255,.12); border-radius:14px; padding:14px;
          background:linear-gradient(180deg,rgba(255,255,255,.05),rgba(255,255,255,.02));
        }
        .summary .prod{display:flex; gap:12px; align-items:center}
        .summary .prod img{width:80px; height:80px; object-fit:cover; border-radius:10px; border:1px solid rgba(255,255,255,.14)}
        .summary h3{margin:0 0 6px}
        .summary p{margin:0; color:var(--muted)}
        .row{display:flex; justify-content:space-between; align-items:center; margin:8px 0}
        .row.total{margin-top:12px; font-size:18px}
        hr{border:none; height:1px; background:linear-gradient(90deg, transparent, rgba(255,255,255,.5), transparent); margin:12px 0}

        @media (max-width:980px){.ck-grid{grid-template-columns:1fr}.grid2{grid-template-columns:1fr}.grid3{grid-template-columns:1fr 1fr 1fr}}
      `}</style>
    </main>
  );
}
